import { Button, Card, Table, Tag } from "@caprikorn/dynamo-ui";
import { createColumnHelper } from "@tanstack/react-table";
import { type FC, useMemo, useState } from "react";

import { DataExportDrawer } from "@components/DataExportDrawer/DataExportDrawer";
import { Graph } from "@components/Graph/Graph";
import type {
  GraphDataContent,
  GraphProps,
} from "@components/Graph/Graph.types";
import { Download } from "@icons";
import { getSemanticType } from "@utils/miscellaneous.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./DashBoardGraph.module.css";

const columnHelper = createColumnHelper<GraphDataContent>();

export const DashboardGraph: FC<GraphProps> = ({ data, biggestValue }) => {
  const { t } = useCustomTranslation(["main", "common"]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const columns = useMemo(
    () => [
      columnHelper.accessor("period", {
        header: t("dashboard.graphCard.table.column.period"),
        cell: ({ row }) =>
          t("capitalizedDate", {
            ns: "common",
            date: new Date(row.original.year, row.original.month - 1, 1),
            formatParams: {
              date: { month: "long", year: "numeric" },
            },
          }),
        size: 500,
      }),
      columnHelper.accessor("consumptionInKwh", {
        header: t("dashboard.graphCard.table.column.consumption"),
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: ({ row }) => (
          <Tag
            color={getSemanticType(row.original.previousMonthGrowthRatio)}
            label={
              row.original.consumptionInKwh
                ? t("numberWithUnit", {
                    ns: "common",
                    value: row.original.consumptionInKwh,
                    measurementUnit: "kWh",
                  })
                : t("notApplicable", { ns: "common" })
            }
            size="sm"
          />
        ),
        size: 500,
      }),
      columnHelper.accessor("previousMonthGrowthRatio", {
        header: t("dashboard.graphCard.table.column.evolution"),
        cell: ({ getValue }) => {
          const value = getValue();
          return value !== null
            ? t("dashboard.graphCard.table.yearlyEvolution", { count: value })
            : t("dashboard.graphCard.table.unavailableEvolution");
        },
        size: 500,
      }),
    ],
    [t],
  );

  return (
    <Card className={classes.container}>
      <h2 className="font-title-sm">{t("dashboard.graphCard.title")}</h2>
      <p className={classes.explanation}>
        {t("dashboard.graphCard.legend.explanation")}
      </p>
      <ul
        aria-label={t("dashboard.graphCard.legend.ariaLabel")}
        className={classes.legend}
      >
        <li>
          <Tag
            color="success"
            label={t("dashboard.graphCard.legend.success")}
            size="md"
            variant="primary"
          />
        </li>
        <li>
          <Tag
            color="warning"
            label={t("dashboard.graphCard.legend.warning")}
            size="md"
            variant="primary"
          />
        </li>
        <li>
          <Tag
            color="error"
            label={t("dashboard.graphCard.legend.danger")}
            size="md"
            variant="primary"
          />
        </li>
        <li>
          <Tag
            color="info"
            label={t("dashboard.graphCard.legend.noData")}
            size="md"
            variant="primary"
          />
        </li>
      </ul>
      <Graph biggestValue={biggestValue} data={data} />
      {data.some(({ consumptionInKwh }) => consumptionInKwh) ? (
        <Table
          columns={columns}
          data={data}
          data-testid="DashBoardConsumptionTable"
          initialPageSize={3}
          initialSortColumn={{ desc: true, id: "period" }}
        >
          <Table.Content />

          <div className={classes.pagination}>
            <Table.Pagination data-testid="Pagination" />
            <Button
              data-testid="PerimeterHeader-download"
              onClick={() => setIsDrawerOpen(true)}
              startIcon={Download}
              variant="secondary"
            >
              {t("dashboard.graphCard.table.downloadButton")}
            </Button>
          </div>
          {isDrawerOpen && (
            <DataExportDrawer
              onHide={() => setIsDrawerOpen(false)}
              perimeterExported={null}
            />
          )}
        </Table>
      ) : (
        <p className={classes.empty}>
          {t("dashboard.graphCard.table.errorFetchData")}
        </p>
      )}
    </Card>
  );
};
